import { Box, Typography, TextField, MenuItem, IconButton, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const MAX_CAROUSEL_CARDS = 10;

export function MediaMessageNodeFields({ localNodeData, updateNodeData }) {
  const caption = localNodeData.caption || '';
  const mediaType = localNodeData.mediaType || 'image';
  const carouselCards = localNodeData.carouselCards || [];

  const handleCaptionChange = (e) => {
    const value = e.target.value || '';
    // Limitamos a 1024 caracteres por seguridad, aunque el TextField ya tenga maxLength
    const safeValue = value.slice(0, 1024);
    updateNodeData('caption', safeValue);
  };

  const handleCarouselCardChange = (index, field, value) => {
    const newCards = [...carouselCards];
    const current = newCards[index] || {};
    newCards[index] = {
      ...current,
      [field]: value,
    };
    updateNodeData('carouselCards', newCards);
  };

  const handleAddCarouselCard = () => {
    if (carouselCards.length >= MAX_CAROUSEL_CARDS) return;

    const newCards = [
      ...carouselCards,
      {
        mediaId: '',
        bodyText: '',
        buttonText: '',
        optionId: '',
      },
    ];
    updateNodeData('carouselCards', newCards);
  };

  const handleRemoveCarouselCard = (index) => {
    const newCards = carouselCards.filter((_, i) => i !== index);
    updateNodeData('carouselCards', newCards);
  };

  return (
    <>
      {/* Tipo de media */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Tipo de medio
        </Typography>
        <TextField
          select
          size="small"
          fullWidth
          value={mediaType}
          onChange={(e) => updateNodeData('mediaType', e.target.value)}
        >
          <MenuItem value="image">Imagen</MenuItem>
          <MenuItem value="video">Video</MenuItem>
          <MenuItem value="document">Documento</MenuItem>
          {/* Nuevo tipo de medio para carrusel */}
          <MenuItem value="carousel">Carrusel interactivo</MenuItem>
        </TextField>
      </Box>

      {/* Caption / cuerpo del mensaje */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Caption (cuerpo del mensaje)
        </Typography>
        <TextField
          value={caption}
          size="small"
          fullWidth
          multiline
          minRows={3}
          inputProps={{ maxLength: 1024 }}
          helperText={`${caption.length} / 1024 caracteres`}
          sx={{
            '& .MuiInputBase-root': {
              alignItems: 'flex-start',
            },
            '& textarea': {
              resize: 'vertical',
              overflow: 'auto',
            },
          }}
          onChange={handleCaptionChange}
        />
      </Box>

      {/* Configuracion para tipos de media simples (imagen/video/documento) */}
      {mediaType !== 'carousel' && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Media ID
            </Typography>
            <TextField
              value={localNodeData.mediaId || ''}
              size="small"
              fullWidth
              placeholder="Identificador del recurso multimedia (mediaId)"
              onChange={(e) => updateNodeData('mediaId', e.target.value)}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Nombre de archivo (opcional)
            </Typography>
            <TextField
              value={localNodeData.fileName || ''}
              size="small"
              fullWidth
              placeholder="Ej: contrato_cliente.pdf"
              onChange={(e) => updateNodeData('fileName', e.target.value)}
            />
          </Box>
        </>
      )}

      {/* Configuracion especifica para Carrusel */}
      {mediaType === 'carousel' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Tarjetas del carrusel
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
            Cada tarjeta representa un abogado u opción del marketplace. El usuario debe seleccionar una tarjeta usando el botón asociado. (máximo {MAX_CAROUSEL_CARDS} tarjetas)
          </Typography>

          {carouselCards.map((card, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Tarjeta #{index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveCarouselCard(index)}
                  disabled={carouselCards.length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <TextField
                label="Media ID de la imagen (Graph)"
                value={card.mediaId || ''}
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                placeholder="mediaId de la imagen para el header de la tarjeta"
                onChange={(e) => handleCarouselCardChange(index, 'mediaId', e.target.value)}
              />

              <TextField
                label="Texto de la tarjeta (body.text)"
                value={card.bodyText || ''}
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 1.5 }}
                onChange={(e) => handleCarouselCardChange(index, 'bodyText', e.target.value)}
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                Botón de selección (sin URL)
              </Typography>

              <TextField
                label="Texto del botón"
                value={card.buttonText || ''}
                size="small"
                fullWidth
                sx={{ mb: 1 }}
                placeholder="Ej: Elegir abogado"
                onChange={(e) => handleCarouselCardChange(index, 'buttonText', e.target.value)}
              />

              <TextField
                label="ID interno de la opción"
                value={card.optionId || ''}
                size="small"
                fullWidth
                placeholder="Ej: abogado_123"
                onChange={(e) => handleCarouselCardChange(index, 'optionId', e.target.value)}
              />
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography
              variant="caption"
              color={carouselCards.length >= MAX_CAROUSEL_CARDS ? 'error' : 'textSecondary'}
            >
              {carouselCards.length}/{MAX_CAROUSEL_CARDS} tarjetas
            </Typography>
            <IconButton
              size="small"
              onClick={handleAddCarouselCard}
              disabled={carouselCards.length >= MAX_CAROUSEL_CARDS}
            >
              <Typography
                variant="body2"
                color={carouselCards.length >= MAX_CAROUSEL_CARDS ? 'textSecondary' : 'primary'}
              >
                + Agregar tarjeta
              </Typography>
            </IconButton>
          </Box>
        </Box>
      )}
    </>
  );
}
